/**
 * Per-tab POS client instance UUID for X-Client-Instance-Id (server cart scope).
 *
 * - Stored in sessionStorage so each tab/window gets its own id; survives refresh
 *   in the same tab but not across distinct tab processes.
 * - broadcastChannel coordinates duplicate-tab / cloned sessionStorage: the tab with
 *   the lexicographically smaller per-document token keeps the shared id; others mint
 *   a new uuid and persist it.
 */

const STORAGE_KEY = 'blu_pos_client_instance_id'
const CHANNEL_NAME = 'blu_pos_client_instance_v1'
const SETTLE_MS = 120

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

let activeId = null
/** Per full page load; not persisted - distinguishes tabs that share cloned sessionStorage */
let docToken = null
let broadcastChannel = null
let initializationPromise = null

function randomUuidV4 () {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

function isValidUuid (s) {
  return typeof s === 'string' && UUID_RE.test(s.trim())
}

function migrateLegacyLocalStorage () {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

function readOrCreateSessionId () {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (raw && isValidUuid(raw)) {
      return raw.trim().toLowerCase()
    }
  } catch {
    // private mode / blocked storage
  }
  const created = randomUuidV4().toLowerCase()
  try {
    window.sessionStorage.setItem(STORAGE_KEY, created)
  } catch {
    // caller still gets in-memory id for this document
  }
  return created
}

function ensureDocToken () {
  if (!docToken) {
    docToken = randomUuidV4().toLowerCase()
  }
  return docToken
}

function persistActiveId (id) {
  activeId = id
  try {
    window.sessionStorage.setItem(STORAGE_KEY, id)
  } catch {
    // ignore
  }
}

function bumpToNewId () {
  const next = randomUuidV4().toLowerCase()
  persistActiveId(next)
  try {
    broadcastChannel?.postMessage({ id: activeId, doc: ensureDocToken() })
  } catch {
    // ignore
  }
}

function handlePeerMessage (ev) {
  const data = ev?.data
  if (!data || typeof data !== 'object') return
  const id = data.id
  const doc = data.doc
  if (!isValidUuid(id) || typeof doc !== 'string' || !isValidUuid(doc)) return
  const mine = ensureDocToken()
  const peerId = id.trim().toLowerCase()
  const current = activeId
  if (!current) return
  if (peerId !== current) return
  if (doc === mine) return
  if (mine < doc) return
  bumpToNewId()
}

function openBroadcastChannel () {
  if (broadcastChannel || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return
  }
  try {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME)
    broadcastChannel.onmessage = handlePeerMessage
  } catch {
    broadcastChannel = null
  }
}

function sleep (ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function initializeClientInstance () {
  try {
    migrateLegacyLocalStorage()
    activeId = readOrCreateSessionId()
    ensureDocToken()
    openBroadcastChannel()

    if (broadcastChannel) {
      try {
        broadcastChannel.postMessage({ id: activeId, doc: docToken })
      } catch {
        // ignore
      }
      await sleep(SETTLE_MS)
    }
  } catch {
    activeId = activeId || randomUuidV4().toLowerCase()
  }
}

/**
 * Resolves the client instance id for this tab (awaits initial peer coordination when
 * BroadcastChannel is available).
 *
 * @returns {Promise<string>}
 */
export async function resolvePosClientInstanceId () {
  if (typeof window === 'undefined') {
    return randomUuidV4().toLowerCase()
  }
  if (!initializationPromise) {
    initializationPromise = initializeClientInstance()
  }
  await initializationPromise
  return activeId || readOrCreateSessionId()
}

/**
 * Synchronous read of the current id for this document. Prefer {@link resolvePosClientInstanceId}
 * before relying on cart APIs. While initialization is in flight, this returns the provisional
 * sessionStorage value once {@link activeId} has been set (first tick of init).
 *
 * @returns {string} Lowercase UUID v4
 */
export function getOrCreatePosClientInstanceId () {
  if (activeId) return activeId
  if (typeof window === 'undefined') {
    return randomUuidV4().toLowerCase()
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (raw && isValidUuid(raw)) {
      return raw.trim().toLowerCase()
    }
  } catch {
    // ignore
  }
  return randomUuidV4().toLowerCase()
}
